/**
 * Wires the browser Web SDK tracker (cdp store) to real page signals:
 *   • web_page_view      — on every route change (+ initial load)
 *   • scroll_depth       — 50% / 100% milestones, once per page
 *   • time_on_page       — seconds on a route, on leave / tab-hide
 *   • product_impression — a product card scrolled into view (data-cdp-impression="product:<id>")
 *   • banner_view        — the promo banner scrolled into view (data-cdp-impression="banner:<placement>")
 *
 * Impressions are captured generically via a single IntersectionObserver over any
 * element tagged `data-cdp-impression="<type>:<id>"`, so components only add the
 * attribute (no imports). All events are behavioral (never commerce/value events),
 * so they don't double-count the server's viewed/cart/checkout events.
 *
 * Pure helper `scrollDepthMilestone` is unit-tested; the rest is DOM wiring.
 */
import { onBeforeUnmount, onMounted } from "vue";
import type { Router } from "vue-router";
import { useCdpStore } from "../stores/cdp";

/** Which milestone (50|100) a scroll fraction crosses that hasn't fired yet, else null. */
export function scrollDepthMilestone(pct: number, fired: ReadonlySet<number>): number | null {
  for (const m of [100, 50]) {
    if (pct >= (m === 100 ? 95 : m) && !fired.has(m)) return m;
  }
  return null;
}

/**
 * Parse a `data-cdp-reco-click="<zone>:<product_id>"` value into a recommendation click.
 * The zone name itself may contain dots (e.g. `home.popular`) but never a colon, so we
 * split on the FIRST colon only. Returns null for malformed/empty values.
 */
export function parseRecoClick(raw: string): { zone: string; product_id: string } | null {
  const idx = raw.indexOf(":");
  if (idx <= 0) return null;
  const zone = raw.slice(0, idx);
  const product_id = raw.slice(idx + 1);
  if (!zone || !product_id) return null;
  return { zone, product_id };
}

export function useCdpTracking(router: Router): void {
  const cdp = useCdpStore();

  let enteredAt = Date.now();
  let currentUrl = typeof window !== "undefined" ? window.location.pathname : "/";
  const scrollFired = new Set<number>();
  const impressionsFired = new Set<string>();
  let io: IntersectionObserver | null = null;
  let mo: MutationObserver | null = null;
  let scrollScheduled = false;

  function pageView(url: string, title?: string): void {
    cdp.track("web_page_view", { url, title: title ?? document.title, referrer: document.referrer || null });
  }

  function flushTimeOnPage(): void {
    const seconds = Math.round((Date.now() - enteredAt) / 1000);
    if (seconds > 0) cdp.track("time_on_page", { url: currentUrl, seconds });
  }

  function onScroll(): void {
    if (scrollScheduled) return;
    scrollScheduled = true;
    requestAnimationFrame(() => {
      scrollScheduled = false;
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      if (total <= 0) return;
      const pct = Math.round(((el.scrollTop + el.clientHeight) / el.scrollHeight) * 100);
      const m = scrollDepthMilestone(pct, scrollFired);
      if (m != null) {
        scrollFired.add(m);
        cdp.track("scroll_depth", { depth: m, url: currentUrl });
      }
    });
  }

  function observeImpressionTargets(): void {
    if (!io || typeof document === "undefined") return;
    document.querySelectorAll("[data-cdp-impression]").forEach((el) => {
      const key = el.getAttribute("data-cdp-impression") ?? "";
      if (key && !impressionsFired.has(key)) io!.observe(el);
    });
  }

  function onImpression(entries: IntersectionObserverEntry[]): void {
    for (const e of entries) {
      if (!e.isIntersecting) continue;
      const key = e.target.getAttribute("data-cdp-impression") ?? "";
      if (!key || impressionsFired.has(key)) continue;
      impressionsFired.add(key);
      io?.unobserve(e.target);
      const [type, ...rest] = key.split(":");
      const id = rest.join(":");
      if (type === "product") cdp.track("product_impression", { product_id: id, url: currentUrl });
      else if (type === "banner") cdp.track("banner_view", { placement: id || "home", url: currentUrl });
      // Показ блока рекомендаций (зона показа): один impression на видимый блок → питает
      // CTR по зонам во вкладке «Метрики» (event_type recommendation.impression, payload.zone).
      else if (type === "reco") cdp.track("recommendation.impression", { zone: id, url: currentUrl });
    }
  }

  /**
   * Клик по карточке в блоке рекомендаций. Делегированный слушатель: любой клик внутри элемента
   * с `data-cdp-reco-click="<zone>:<id>"` (у нас это карточка-ссылка) шлёт recommendation.click.
   * Клики по внутренним кнопкам карточки (плюс/минус/в корзину) пропускаем — это не переход по
   * рекомендации, а отдельное действие; так CTR не завышается.
   */
  function onRecoClick(ev: Event): void {
    const target = ev.target as HTMLElement | null;
    if (!target || typeof target.closest !== "function") return;
    if (target.closest("button")) return;
    const el = target.closest("[data-cdp-reco-click]");
    if (!el) return;
    const parsed = parseRecoClick(el.getAttribute("data-cdp-reco-click") ?? "");
    if (parsed) cdp.track("recommendation.click", { ...parsed, url: currentUrl });
  }

  onMounted(() => {
    // Fire the tracker up (fetch web-context, start experiences polling).
    void cdp.init();

    // Initial page view.
    currentUrl = window.location.pathname;
    enteredAt = Date.now();
    pageView(currentUrl);

    window.addEventListener("scroll", onScroll, { passive: true });
    // Клики по рекомендациям — делегированно на весь документ (capture, чтобы поймать до навигации).
    document.addEventListener("click", onRecoClick, { capture: true });

    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(onImpression, { threshold: 0.5 });
      observeImpressionTargets();
      mo = new MutationObserver(() => observeImpressionTargets());
      mo.observe(document.body, { childList: true, subtree: true });
    }

    const onHide = () => {
      if (document.visibilityState === "hidden") {
        flushTimeOnPage();
        cdp.flush();
      }
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", () => {
      flushTimeOnPage();
      cdp.flush();
    });

    // Route changes: close out the old page, open the new one.
    router.afterEach((to) => {
      flushTimeOnPage();
      currentUrl = to.fullPath;
      enteredAt = Date.now();
      scrollFired.clear();
      impressionsFired.clear();
      // Let the new view render, then re-arm impression targets + page view.
      window.setTimeout(() => {
        pageView(currentUrl, typeof to.meta.title === "string" ? to.meta.title : undefined);
        observeImpressionTargets();
      }, 0);
    });
  });

  onBeforeUnmount(() => {
    window.removeEventListener("scroll", onScroll);
    document.removeEventListener("click", onRecoClick, { capture: true } as EventListenerOptions);
    io?.disconnect();
    mo?.disconnect();
    flushTimeOnPage();
    cdp.flush();
  });
}
